import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

export const Responsibility = () => {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="chapter-responsibility">
      <article className="max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <header className="mb-16 animate-fade-in">
          <p className="text-xs font-sans font-medium tracking-widest uppercase text-stone-400 mb-4">
            Chapter
          </p>
          <h1 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl text-stone-800 tracking-tight leading-[1.2]">
            Responsibility
          </h1>
        </header>

        <div className="prose-living space-y-8 animate-fade-in-delay-1">
          <p>
            Responsibility often arrives without invitation. 
            A parent who needs help. A child who needs guidance. 
            A situation that falls to you because no one else can carry it.
          </p>

          <p>
            Sometimes this weight is chosen. Sometimes it's inherited. 
            Either way, it shapes how you move through the world.
          </p>

          <p>
            This chapter is for those who carry things for others. 
            Not to add more weight — but to help you see what you're already holding.
          </p>

          <div className="py-8 border-t border-b border-stone-100 my-12 space-y-6">
            <h2 className="font-serif text-xl text-stone-700">
              Reflections to consider:
            </h2>
            <ul className="space-y-4 text-stone-600">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What responsibilities did you choose? Which ones chose you?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>Who depends on you — in ways large or small?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What would happen if something changed suddenly?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>Is there anything you wish someone else knew — just in case?</span>
              </li>
            </ul>
          </div>

          <p>
            Responsibility can feel heavy when it's unclear. 
            It often feels lighter once it's seen.
          </p>

          <p>
            This is a place to notice what you carry — 
            and perhaps, to share the weight in a way that feels right.
          </p>
        </div>

        <nav className="mt-16 pt-8 border-t border-stone-100 animate-fade-in-delay-2">
          <p className="text-sm font-sans text-stone-400 mb-4">Explore other chapters</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/living-book/chapters/stability"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-stability"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Stability
            </Link>
            <Link
              to="/living-book/chapters/transition"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-transition"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Transition
            </Link>
            <Link
              to="/living-book/chapters/legacy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-legacy"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Legacy
            </Link>
          </div>
        </nav>
      </article>
    </div>
  );
};

export default Responsibility;
